import { collection, addDoc, doc, getDoc, query, where, getDocs, updateDoc, deleteDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { auth, db } from '../../sdk_initialization';
import { sendEmail } from './emailService';

function appsCol() { return collection(db, 'boothApplications'); }

export async function createBoothApplication({ vendorId, eventId, layoutId, boothsRequested, acknowledgement }) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  if (!eventId) throw new Error('Missing eventId');
  if (!layoutId) throw new Error('Missing layoutId');
  const normalizedBooths = (boothsRequested || []).map(b => ({
    level: String(b.level || ''),
    position: String(b.position || ''),
    itemType: String(b.itemType || ''),
    notes: String(b.notes || ''),
    estimate: Number.isFinite(b.estimate) ? Number(b.estimate) : 0
  }));
  const payload = {
    userUid: user.uid,
    vendorId: vendorId || null,
    eventId,
    layoutId,
    status: 'Pending',
  boothsRequested: normalizedBooths, // [{level, position, itemType, notes?, estimate}]
  numBooths: normalizedBooths.length,
    acknowledgement: !!acknowledgement,
    submittedAt: serverTimestamp(),
    reviewedAt: null,
    reviewedBy: null,
    reviewNote: '',
  };
  try {
    const ref = await addDoc(appsCol(), payload);
    return { id: ref.id, ...payload };
  } catch (err) {
    console.error('[createBoothApplication] Firestore addDoc failed', { error: err, payload });
    throw new Error(err?.message || 'Failed to create application');
  }
}

export async function getBoothApplication(id) {
  const snap = await getDoc(doc(db, 'boothApplications', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function listBoothApplications({ status, limitCount = 100 } = {}) {
  let qRef = query(appsCol(), orderBy('submittedAt', 'desc'), limit(limitCount));
  if (status) {
    qRef = query(appsCol(), where('status', '==', status), orderBy('submittedAt', 'desc'), limit(limitCount));
  }
  const snaps = await getDocs(qRef);
  return snaps.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function listPendingApplications(limitCount = 100) {
  return listBoothApplications({ status: 'Pending', limitCount });
}

/**
 * List applications for a given event (admin)
 * Returns minimal fields; caller can filter/group by vendorId
 */
export async function listApplicationsForEvent(eventId, { limitCount = 1000 } = {}) {
  if (!eventId) return [];
  try {
    const qRef = query(
      appsCol(),
      where('eventId', '==', eventId),
      limit(limitCount)
    );
    const snaps = await getDocs(qRef);
    return snaps.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[listApplicationsForEvent] error', err?.message || err);
    return [];
  }
}

export async function approveBoothApplication(id, note = '') {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'boothApplications', id);
  await updateDoc(ref, {
    status: 'Approved',
    reviewedAt: serverTimestamp(),
    reviewedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    reviewNote: note || '',
  });
}

export async function rejectBoothApplication(id, note = '') {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'boothApplications', id);
  await updateDoc(ref, {
    status: 'Rejected',
    reviewedAt: serverTimestamp(),
    reviewedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    reviewNote: note || '',
  });
}

export async function setPendingBoothApplication(id, note = '') {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'boothApplications', id);
  await updateDoc(ref, {
    status: 'Pending',
    reviewedAt: serverTimestamp(),
    reviewedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    reviewNote: note || '',
  });
}

export async function deleteBoothApplication(id) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'boothApplications', id);
  await deleteDoc(ref);
}

export async function getMyLatestApplication() {
  const user = auth.currentUser;
  if (!user) return null;
  const qRef = query(appsCol(), where('userUid', '==', user.uid), orderBy('submittedAt', 'desc'), limit(1));
  const snaps = await getDocs(qRef);
  if (snaps.empty) return null;
  const d = snaps.docs[0];
  return { id: d.id, ...d.data() };
}

export async function getMyLatestApplicationForEvent(eventId) {
  const user = auth.currentUser;
  if (!user || !eventId) return null;
  try {
    // Preferred: server-side filter by eventId
    const qRef = query(
      appsCol(),
      where('userUid', '==', user.uid),
      where('eventId', '==', eventId),
      orderBy('submittedAt', 'desc'),
      limit(1)
    );
    const snaps = await getDocs(qRef);
    if (!snaps.empty) {
      const d = snaps.docs[0];
      return { id: d.id, ...d.data() };
    }
    return null;
  } catch (err) {
    // If a composite index is missing, fall back to fetching recent user apps and filter client-side
    console.warn('[getMyLatestApplicationForEvent] falling back due to error', err?.message || err);
    const altRef = query(
      appsCol(),
      where('userUid', '==', user.uid),
      orderBy('submittedAt', 'desc'),
      limit(20)
    );
    const snaps = await getDocs(altRef);
    const match = snaps.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .find(d => d.eventId === eventId);
    return match || null;
  }
}

export async function getLatestApplicationForVendorEvent(vendorId, eventId) {
  if (!vendorId || !eventId) return null;
  try {
    const qRef = query(
      appsCol(),
      where('vendorId', '==', vendorId),
      where('eventId', '==', eventId),
      orderBy('submittedAt', 'desc'),
      limit(1)
    );
    const snaps = await getDocs(qRef);
    if (!snaps.empty) {
      const d = snaps.docs[0];
      return { id: d.id, ...d.data() };
    }
    return null;
  } catch (err) {
    console.warn('[getLatestApplicationForVendorEvent] index missing; using vendor-only fallback', err?.message || err);
    // Fallback: fetch by vendorId only (no orderBy to avoid index), then filter/sort client-side by eventId and submittedAt
    try {
      const altRef = query(
        appsCol(),
        where('vendorId', '==', vendorId),
        limit(50)
      );
      const snaps = await getDocs(altRef);
      const docs = snaps.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.eventId === eventId)
        .sort((a, b) => {
          const ta = a.submittedAt;
          const tb = b.submittedAt;
          const ma = ta && typeof ta.toMillis === 'function' ? ta.toMillis() : (ta?.seconds ? ta.seconds * 1000 : 0);
          const mb = tb && typeof tb.toMillis === 'function' ? tb.toMillis() : (tb?.seconds ? tb.seconds * 1000 : 0);
          return mb - ma;
        });
      return docs[0] || null;
    } catch (e2) {
      console.error('[getLatestApplicationForVendorEvent] fallback failed', e2?.message || e2);
      return null;
    }
  }
}

export async function updateMyPendingApplication(appId, { boothsRequested, acknowledgement, vendorId }) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  if (!appId) throw new Error('Missing application id');
  const normalizedBooths = (boothsRequested || []).map(b => ({
    level: String(b.level || ''),
    position: String(b.position || ''),
    itemType: String(b.itemType || ''),
    notes: String(b.notes || ''),
    estimate: Number.isFinite(b.estimate) ? Number(b.estimate) : 0
  }));
  const ref = doc(db, 'boothApplications', appId);
  try {
    const update = {
      boothsRequested: normalizedBooths,
      numBooths: normalizedBooths.length,
      acknowledgement: !!acknowledgement,
      updatedAt: serverTimestamp(),
    };
    if (vendorId !== undefined) {
      update.vendorId = vendorId || null;
    }
    await updateDoc(ref, update);
  } catch (err) {
    console.error('[updateMyPendingApplication] Firestore updateDoc failed', { error: err, appId, boothsCount: normalizedBooths.length });
    throw new Error(err?.message || 'Failed to update application');
  }
}

// Build approval email subject/body for an application
export async function sendApprovalEmail(app = {}, vendor = {}, event = {}) {

  try {
    const to = vendor?.email || '';
    const eventNameLine = event?.eventName || event?.name || 'this event';
    const statusLine = app?.status || 'Approved';
    const vendorNameLine = vendor?.name || vendor?.businessName || 'Vendor';
    const nextStepsText = (event?.approvalEmailText || '').toString().trim();
    const defaultNextSteps = 'When booth registration opens, you will be able to purchase booths from the portal.\nSign in to your account and go to the Vendor Dashboard → Buy Booths to select and purchase available booths.';
    const nextStepsHtml = nextStepsText ? nextStepsText.replace(/\r?\n/g, '<br/>') : defaultNextSteps.replace(/\r?\n/g, '<br/>');
    const adminEmail = (event?.eventAdminContactEmail || '').toString().trim();
    const booths = Array.isArray(app?.boothsRequested) ? app.boothsRequested : [];
    const boothCount = booths.length || (Number.isFinite(app?.numBooths) ? Number(app.numBooths) : 0);
    const boothRows = booths
      .map((b) => {
        const level = (b?.level || '-').toString();
        const cornerOrInline = (b?.position || '-').toString();
        const type = (b?.itemType || '-').toString();
        const notes = (b?.notes || '').toString().trim();
        return `${level} - ${cornerOrInline} - ${type}${notes ? ` - Description: ${notes}` : ''}`;
      });
    const boothBlock = booths.length > 0
      ? `<ol style="margin-left: 20px;">` + boothRows.map(row => `<li>${row}</li>`).join('') + `</ol>`
      : '-';
    const subj = `Application Update: ${statusLine} - ${eventNameLine}`;
    const body = `
      <p>Dear Vendor [${vendorNameLine}],</p>
      <p>Thank you for your application as a vendor for the following event. We would like to share an update with you.</p>
      <p><strong>Event Name:</strong> ${eventNameLine}</p>
      <p><strong>Booth Info (${boothCount}):</strong><br/>${boothBlock}</p>
      <p><strong>Application Status:</strong> ${statusLine}.</p>
      <p>${nextStepsHtml}</p>
      <p>Thank you and for any questions, please reach out to the event admin.</p>
      <p>Event Management Team,<br/>${adminEmail || '-'}</p>
    `;
    await sendEmail({ to, subject: subj, type: 'HTML', body });

  } catch (err) {
    console.error('[sendApprovalEmail] failed to send email', { error: err, appId: app?.id, vendorId: vendor?.id });
    return false;
  } 
  return true;
}

/**
 * Update the latest pending application for a given vendor+event.
 * Finds the most recent application where vendorId==vendorId && eventId==eventId && status=='Pending'
 * and applies the same normalized update as `updateMyPendingApplication`.
 */
export async function updatePendingApplicationForVendor(vendorId, eventId, { boothsRequested, acknowledgement }) {
  if (!vendorId || !eventId) throw new Error('Missing vendorId or eventId');
  // Find the latest pending application for this vendor/event
  const latest = await getLatestApplicationForVendorEvent(vendorId, eventId);
  if (!latest || !latest.id) throw new Error('No pending application found for vendor/event');
  if (latest.status !== 'Pending') throw new Error('Latest application is not pending');

  const normalizedBooths = (boothsRequested || []).map(b => ({
    level: String(b.level || ''),
    position: String(b.position || ''),
    itemType: String(b.itemType || ''),
    notes: String(b.notes || ''),
    estimate: Number.isFinite(b.estimate) ? Number(b.estimate) : 0
  }));

  const ref = doc(db, 'boothApplications', latest.id);
  try {
    const update = {
      boothsRequested: normalizedBooths,
      numBooths: normalizedBooths.length,
      acknowledgement: !!acknowledgement,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(ref, update);
    return { id: latest.id, ...update };
  } catch (err) {
    console.error('[updatePendingApplicationForVendor] Firestore updateDoc failed', { error: err, vendorId, eventId });
    // Provide a clearer error for permission issues so callers can surface actionable hints
    if (err?.code === 'permission-denied' || (err?.message && err.message.toLowerCase().includes('permission'))) {
      throw new Error('Permission denied updating application. Ensure your account is listed in the vendor\'s `memberUids` and that Firestore rules have been deployed to allow vendor-member updates.');
    }
    throw new Error(err?.message || 'Failed to update vendor application');
  }
}

export async function listApplicationsForVendor(vendorId, { limitCount = 100 } = {}) {
  if (!vendorId) return [];
  try {
    const qRef = query(
      appsCol(),
      where('vendorId', '==', vendorId),
      limit(limitCount)
    );
    const snaps = await getDocs(qRef);
    return snaps.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[listApplicationsForVendor] error', err?.message || err);
    return [];
  }
}

  // export async function listApplicationsForUser(userUid, { limitCount = 100 } = {}) {
  //   if (!userUid) return [];
  //   try {
  //     const qRef = query(
  //       appsCol(),
  //       where('userUid', '==', userUid),
  //       limit(limitCount)
  //     );
  //     const snaps = await getDocs(qRef);
  //     return snaps.docs.map(d => ({ id: d.id, ...d.data() }));
  //   } catch (err) {
  //     console.warn('[listApplicationsForUser] error', err?.message || err);
  //     return [];
  //   }
  // }
